import React from "react";
import { Button, Box, keyframes } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

interface UpsellAnimationButtonProps {
  onClick?: () => void;
  label?: string;
  startIcon?: React.ReactNode;
}

// Keyframe animations
const twinkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.5); }
  50% { box-shadow: 0 0 40px rgba(251, 191, 36, 0.8); }
`;

const bounceGentle = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

export const UpsellAnimationButton: React.FC<UpsellAnimationButtonProps> = ({
  onClick,
  label = "🎯 Upgrade Now!",
  startIcon,
}) => {
  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      {/* Decorative floating stars */}
      <StarIcon
        sx={{
          position: "absolute",
          top: -8,
          left: -8,
          fontSize: 14,
          color: "#facc15",
          animation: `${twinkle} 2s ease-in-out infinite`,
          animationDelay: "0s",
        }}
      />
      <StarIcon
        sx={{
          position: "absolute",
          top: -4,
          right: -4,
          fontSize: 10,
          color: "#fde047",
          animation: `${twinkle} 2s ease-in-out infinite`,
          animationDelay: "0.7s",
        }}
      />
      <AutoAwesomeIcon
        sx={{
          position: "absolute",
          bottom: -8,
          left: 8,
          fontSize: 14,
          color: "#fde047",
          animation: `${twinkle} 2s ease-in-out infinite`,
          animationDelay: "0.5s",
        }}
      />
      <AutoAwesomeIcon
        sx={{
          position: "absolute",
          bottom: -4,
          right: 16,
          fontSize: 10,
          color: "#facc15",
          animation: `${twinkle} 2s ease-in-out infinite`,
          animationDelay: "1s",
        }}
      />

      <Button
        variant="contained"
        onClick={onClick}
        startIcon={
          startIcon ? (
            startIcon
          ) : (
            <TrendingUpIcon
              sx={{
                animation: `${bounceGentle} 2s ease-in-out infinite`,
              }}
            />
          )
        }
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(90deg, #facc15, #fb923c, #facc15)",
          backgroundSize: "200% 100%",
          color: "#000",
          fontWeight: "bold",

          px: 2,
          py: 1,
          borderRadius: "50px",
          border: "3px solid #fde047",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          transition: "all 0.3s ease",
          animation: `${pulseGlow} 2s ease-in-out infinite`,
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            background: "linear-gradient(90deg, #facc15, #fb923c, #facc15)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
            animation: `${shimmer} 3s ease-in-out infinite`,
          },
        }}
      >
        {label}
      </Button>
    </Box>
  );
};
